// Material UI
import { InputAdornment } from '@material-ui/core';

// Shared
import {
  getTextWidth,
  formatCurrency,
  formatDate,
  formatPorcentage
} from '../shared/helper';

// Components
import Actions from '../components/listing/Actions/Actions';
import Filter from '../components/listing/Filter/Filter';
import FormInput from '../components/detail/FormInput/FormInput';

// Taxonomies
import {
  salesChannelCore,
  salesChannelFilter
} from '../taxonomies/salesChannel';
import { yearCore, yearFilter } from '../taxonomies/year';
import { countryCore, countryFilter } from '../taxonomies/country';
import { trimCore, trimFilter } from './trim';
import { modelCore, modelFilter } from '../taxonomies/model';
import { engineCore, engineFilter } from '../taxonomies/engine';
import {
  salesVersionCore,
  salesVersionFilter
} from '../taxonomies/salesVersion';
import { gearboxCore, gearboxFilter } from '../taxonomies/gearbox';
import { programCore, programFilter } from '../taxonomies/program';
import {
  pricingGroupCore,
  pricingGroupFilter
} from '../taxonomies/pricingGroup';

// Pricing core
export function pricingCore() {
  const endpoint = '/pricing';
  const detailId = 'discountId';

  const getDetailId = (data = null) => {
    if (!data) {
      return;
    }

    if (Array.isArray(data)) {
      return data[0][detailId];
    }

    return data[detailId];
  };

  return {
    endpoint,
    detailId,
    getDetailId: getDetailId
  };
}

// Pricing listing
export function pricingListing() {

  // Initial sort
  const initialSort = { field: 'id', sort: 'asc' };

  // Initial filter values
  const initialFilters = { DescriptionSearchType: 'C', DerivativeCodeSearchType: 'C' };

  // Get columns
  // columnsCustomized is used in case there is a hidden column
  const getColumns = (rows, columnsCustomized) => {
    const columns =
      columnsCustomized.length > 0
        ? columnsCustomized
        : [
            {
              field: 'actions',
              headerName: 'Actions',
              sortable: false,
              renderCell: function (params) {
                return (
                  <Actions
                    typeOfData="data-type"
                    id={params.row.id}
                    endpoint="/pricing"
                    deleteApiEndpoint="/pricing"
                  />
                );
              },
              hideFromColumnsSelector: true
            },
            {
              field: 'id',
              headerName: 'ID',
              hide: true
            },
            { field: 'country', headerName: 'Country' },
            { field: 'year', headerName: 'Year' },
            { field: 'description', headerName: 'Description' },
            { field: 'salesChannel', headerName: 'Sales Channel' },
            { field: 'trim', headerName: 'Vehicle' },
            {
              field: 'startDate',
              headerName: 'Start Date',
              valueFormatter: function (params) {
                return formatDate(params.value);
              }
            },
            {
              field: 'endDate',
              headerName: 'End Date',
              valueFormatter: function (params) {
                return formatDate(params.value);
              }
            },
            { field: 'derivativeCode', headerName: 'Derivative Code' },
            {
              field: 'price',
              headerName: 'Price',
              valueFormatter: function (params) {
                return formatCurrency(params.value);
              }
            },
            {
              field: 'discountTrim',
              headerName: 'Base Discount',
              valueFormatter: function (params) {
                return formatPorcentage(params.value);
              }
            },
            {
              field: 'discountPackage',
              headerName: 'Package Discount',
              valueFormatter: function (params) {
                return formatPorcentage(params.value);
              }
            },
            {
              field: 'discountOption',
              headerName: 'Option Discount',
              valueFormatter: function (params) {
                return formatPorcentage(params.value);
              }
            },
            {
              field: 'discountAccessory',
              headerName: 'Accessory Discount',
              valueFormatter: function (params) {
                return formatPorcentage(params.value);
              }
            },
            {
              field: 'discountColor',
              headerName: 'Color Discount',
              valueFormatter: function (params) {
                return formatPorcentage(params.value);
              }
            },
            {
              field: 'discountUpholstery',
              headerName: 'Upholstery Discount',
              valueFormatter: function (params) {
                return formatPorcentage(params.value);
              }
            }
          ];

    const columnsResized = columns;

    columns.forEach((column, i) => {
      const id = column.field;
      let text = column.headerName;

      const textWidth = getTextWidth(text);
      columnsResized[i].width = textWidth;

      rows.forEach((row, j) => {
        if (row[id]) {
          let text = row[id];

          // Add extra chars that might appear on the price
          if (id === 'price') {
            text += '$,.00';
          }

          const textWidth = getTextWidth(text);

          if (columnsResized[i].width < textWidth) {
            columnsResized[i].width = textWidth;
          }
        }
      });
    });

    return columnsResized;
  };

  // Get rows
  const getRows = (data) => {
    const rows = [];

    data.forEach((row, i) => {
      // Text/numeric values
      rows[i] = {
        id: row.discountId,
        country: row.countryName,
        year: row.yearDescription,
        derivativeCode: row.discountCode,
        price: row.trimPrice,
        startDate: row.discountStartDate,
        endDate: row.discountEndDate,
        salesChannel: row.salesChannelDescription,
        description: row.discountDescription,
        discountTrim: row.discountTrim,
        discountPackage: row.discountPackage,
        discountOption: row.discountOption,
        discountAccessory: row.discountAccessory,
        discountColor: row.discountColor,
        discountUpholstery: row.discountUpholstery
      };

      // Object values
      const objects = {
        trim: row.trimDescription
      };

      // Get the text/value from the default culture
      Object.entries(objects).forEach(([j, prop]) => {
        prop.translations.forEach((culture, k) => {
          if (culture.defaultCulture === true) {
            rows[i][j] = culture.text;
          }
        });
      });
    });

    return rows;
  };

  // Get filters
  const getFilters = () => {
    // Taxonomies
    const salesChannelCore_ = salesChannelCore();
    const salesChannelFilter_ = salesChannelFilter();

    const countryCore_ = countryCore();
    const countryFilter_ = countryFilter();

    const yearCore_ = yearCore();
    const yearFilter_ = yearFilter();

    const modelCore_ = modelCore();
    const modelFilter_ = modelFilter();

    const engineCore_ = engineCore();
    const engineFilter_ = engineFilter();

    const salesVersionCore_ = salesVersionCore();
    const salesVersionFilter_ = salesVersionFilter();

    const gearboxCore_ = gearboxCore();
    const gearboxFilter_ = gearboxFilter();

    const programCore_ = programCore();
    const programFilter_ = programFilter();

    return [
      [
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={salesChannelFilter_.parameter}
              label="Sales Channel"
              endpoint={salesChannelCore_.endpointSimple}
              getRows={salesChannelFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={countryFilter_.parameter}
              label="Country"
              endpoint={countryCore_.endpoint}
              getRows={countryFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={yearFilter_.parameter}
              label="Year"
              endpoint={yearCore_.endpoint}
              getRows={yearFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={modelFilter_.parameter}
              label="Model"
              endpoint={modelCore_.endpointSimple}
              getRows={modelFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={engineFilter_.parameter}
              label="Engine"
              endpoint={engineCore_.endpointSimple}
              getRows={engineFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={salesVersionFilter_.parameter}
              label="Sales Version"
              endpoint={salesVersionCore_.endpointSimple}
              getRows={salesVersionFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={gearboxFilter_.parameter}
              label="Transmission"
              endpoint={gearboxCore_.endpointSimple}
              getRows={gearboxFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={programFilter_.parameter}
              label="Program"
              endpoint={programCore_.endpoint}
              getRows={programFilter_.getRows}
            />
          )
        }
      ],
      [
        {
          size: { xs: 12, md: 12, lg: 6 },
          component: (
            <Filter
              type="duo"
              parameter={['DescriptionSearchType', 'Description']}
              label="Description"
            />
          )
        },
        {
          size: { xs: 12, md: 12, lg: 6 },
          component: (
            <Filter
              type="duo"
              parameter={['DerivativeCodeSearchType', 'DerivativeCode']}
              label="Derivative Code"
            />
          )
        }
      ]
    ];
  };

  return {
    initialSort,
    initialFilters,
    getColumns: getColumns,
    getRows: getRows,
    getFilters: getFilters
  };
}

// Pricing detail
export function pricingDetail() {
  const baseUrl = '/pricing';

  const getInputs = (data = null) => {
    // set an object of all the field values
    // where the default state is empty for each
    let detailObj = {
      discountId: '',
      discountDescription: '',
      salesChannelId: '',
      countryId: '',
      yearId: '',
      modelId: '',
      trimId: '', // '' for edit | [] for new
      programId: [],
      pricingGroupId: [],
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      discountTrim: '',
      discountPackage: '',
      discountOption: '',
      discountAccessory: '',
      discountColor: '',
      discountUpholstery: ''
    };

    if (data !== null) {
      // Use the data provided to populate the default value of the fields
      Object.entries(detailObj).forEach(([i, prop]) => {
        detailObj[i] = data[i];
      });
    }

    // Taxonomies
    const salesChannelCore_ = salesChannelCore();
    const salesChannelFilter_ = salesChannelFilter();

    const countryCore_ = countryCore();
    const countryFilter_ = countryFilter();

    const yearCore_ = yearCore();
    const yearFilter_ = yearFilter();

    const modelCore_ = modelCore();
    const modelFilter_ = modelFilter();

    const trimCore_ = trimCore();
    const trimFilter_ = trimFilter();

    const programCore_ = programCore();
    const programFilter_ = programFilter();

    const pricingGroupCore_ = pricingGroupCore();
    const pricingGroupFilter_ = pricingGroupFilter();

    return [
      // Description
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="text"
              parameter="discountDescription"
              label="Description"
              required={true}
              inputValue={detailObj.discountDescription}
            />
          )
        }
      ],
      // Sales Channel
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              parameter={salesChannelCore_.detailId}
              label="Sales Channel"
              required={true}
              endpoint={salesChannelCore_.endpoint}
              getRows={salesChannelFilter_.getRows}
              inputValue={detailObj.salesChannelId}
              isCulturesSource={true}
            />
          )
        }
      ],
      // Country
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              parameter={countryCore_.detailId}
              label="Country"
              required={true}
              endpoint={countryCore_.endpoint}
              getRows={countryFilter_.getRows}
              inputValue={detailObj.countryId}
              isCulturesSource={true}
            />
          )
        }
      ],
      // Year
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              taxonomyName="year"
              parameter={yearCore_.detailId}
              label="Year"
              required={true}
              endpoint={yearCore_.endpoint}
              getRows={yearFilter_.getRows}
              inputValue={detailObj.yearId}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
            />
          )
        }
      ],
      // Model
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              taxonomyName="model"
              parameter={modelCore_.detailId}
              label="Model"
              required={true}
              disabled={true}
              endpoint={modelCore_.endpoint}
              getRows={modelFilter_.getRows}
              inputValue={detailObj.modelId}
              dependOn={[
                { filter: 'countryid', control: 'countryId' },
                { filter: 'yearid', control: 'yearId' }
              ]}
            />
          )
        }
      ],
      // Trim
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type={detailObj.discountId !== '' ? 'select' : 'multiple-select'}
              taxonomyName="trim"
              parameter={trimCore_.detailId}
              label="Vehicle"
              required={true}
              disabled={true}
              endpoint={trimCore_.endpoint}
              getRows={trimFilter_.getRows}
              inputValue={detailObj.trimId}
              dependOn={[
                { filter: 'countryid', control: 'countryId' },
                { filter: 'yearid', control: 'yearId' },
                { filter: 'modelid', control: 'modelId' }
              ]}
              disabledController={{ control: 'applyAllTrim', value: false }}
            />
          )
        }
      ],
      // Apply to all trims
      detailObj.discountId === '' && [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="checkbox"
              label="Apply to all vehicles"
              parameter="applyAllTrim"
              inputValue={false}
            />
          )
        }
      ],
      // Program
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="multiple-select"
              taxonomyName="program"
              parameter={programCore_.detailId}
              label="Program"
              required={true}
              disabled={true}
              endpoint={programCore_.endpoint}
              getRows={programFilter_.getRows}
              inputValue={detailObj.programId}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
            />
          )
        }
      ],
      // Pricing Group
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="multiple-select"
              taxonomyName="program"
              parameter={pricingGroupCore_.detailId}
              label="Pricing Group"
              required={true}
              disabled={true}
              endpoint={pricingGroupCore_.endpoint}
              getRows={pricingGroupFilter_.getRows}
              inputValue={detailObj.pricingGroupId}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
            />
          )
        }
      ],
      // Begin Date
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="datetime"
              parameter="startDate"
              label="Start Date"
              required={true}
              inputValue={detailObj.startDate}
            />
          )
        }
      ],
      // End Date
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="datetime"
              parameter="endDate"
              label="End Date"
              required={true}
              inputValue={detailObj.endDate}
            />
          )
        }
      ],
      // Base Discount
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="text"
              parameter="discountTrim"
              label="Base Discount"
              required={true}
              inputValue={detailObj.discountTrim}
              inputProps={{
                pattern: '[0-9]{1,3}',
                maxLength: 3
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          )
        }
      ],
      // Package Discount
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="text"
              parameter="discountPackage"
              label="Package Discount"
              required={true}
              inputValue={detailObj.discountPackage}
              inputProps={{
                pattern: '[0-9]{1,3}',
                maxLength: 3
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          )
        }
      ],
      // Option Discount
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="text"
              parameter="discountOption"
              label="Option Discount"
              required={true}
              inputValue={detailObj.discountOption}
              inputProps={{
                pattern: '[0-9]{1,3}',
                maxLength: 3
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          )
        }
      ],
      // Accessory Discount
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="text"
              parameter="discountAccessory"
              label="Accessory Discount"
              required={true}
              inputValue={detailObj.discountAccessory}
              inputProps={{
                pattern: '[0-9]{1,3}',
                maxLength: 3
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          )
        }
      ],
      // Color Discount
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="text"
              parameter="discountColor"
              label="Color Discount"
              required={true}
              inputValue={detailObj.discountColor}
              inputProps={{
                pattern: '[0-9]{1,3}',
                maxLength: 3
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          )
        }
      ],
      // Upholstery Discount
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="text"
              parameter="discountUpholstery"
              label="Upholstery Discount"
              required={true}
              inputValue={detailObj.discountUpholstery}
              inputProps={{
                pattern: '[0-9]{1,3}',
                maxLength: 3
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          )
        }
      ]
    ];
  };

  return {
    baseUrl,
    getInputs: getInputs
  };
}
